import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { DoorOpen, Save, Upload, Image as ImageIcon, Plus, X, LogIn, LogOut, Link2, UserPlus, ScrollText } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { Toggle, PageHead } from "@/components/kit";
import { ChannelSelect } from "@/components/ChannelSelect";
import { RoleSelect } from "@/components/RoleSelect";

export default function Welcome() {
  const { guildId } = useGuild();
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  const [uploading, setUploading] = useState("");
  const [roleInput, setRoleInput] = useState("");
  const [btn, setBtn] = useState({ label: "", url: "" });
  const welcomeRef = useRef();
  const goodbyeRef = useRef();
  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { api.get(`/guilds/${guildId}/modules/welcome`).then((r) => setCfg(r.data)); }, [guildId]);
  const saveRaw = async () => { await api.put(`/guilds/${guildId}/modules/welcome`, { enabled: cfg.enabled, config: cfg.config }); toast.success("Saved"); };
  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const c = cfg.config;
  // Functional update to avoid stale-closure bugs when multiple set() calls fire
  // in rapid succession (e.g. typing then clicking a toggle before React commits).
  const set = (k, v) => setCfg((prev) => ({ ...prev, config: { ...prev.config, [k]: v } }));
  const preview = (t, name = "@NewMember") => (t || "").replace(/\{user\}/g, name).replace(/\{count\}/g, "1,204").replace(/\{server\}/g, "Your Server");
  const src = (u) => (u?.startsWith("/") ? backend + u : u);

  const uploadBanner = async (e, key) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Image must be under 8MB"); return; }
    setUploading(key);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/uploads", fd);
      set(key, data.url);
      toast.success("Banner uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(""); }
  };

  const roles = c.join_roles || [];
  const addRole = () => {
    const v = roleInput.trim().replace(/^@/, "");
    if (!v) return;
    setCfg((prev) => {
      const cur = prev.config.join_roles || [];
      if (cur.includes(v)) return prev;
      return { ...prev, config: { ...prev.config, join_roles: [...cur, v] } };
    });
    setRoleInput("");
  };
  const buttons = c.buttons || [];
  const addBtn = () => {
    if (!btn.label.trim() || !/^https?:\/\//.test(btn.url.trim())) { toast.error("Enter a label and a URL starting with http(s)://"); return; }
    setCfg((prev) => {
      const cur = prev.config.buttons || [];
      if (cur.length >= 5) { toast.error("Max 5 buttons"); return prev; }
      return { ...prev, config: { ...prev.config, buttons: [...cur, { label: btn.label.trim(), url: btn.url.trim() }] } };
    });
    setBtn({ label: "", url: "" });
  };

  const field = "mt-1 w-full bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm";
  const lbl = "text-xs uppercase tracking-wider text-zinc-500";

  const Dropzone = ({ value, k, refEl }) => (
    <div>
      <label className={lbl}>Banner image</label>
      <div onClick={() => refEl.current?.click()} data-testid={`${k}-dropzone`} className="mt-1 border-2 border-dashed border-zinc-700 rounded-xl p-4 text-center cursor-pointer hover:border-volt transition-colors">
        {value ? <img src={src(value)} alt="" className="max-h-28 mx-auto rounded" /> : <><Upload className="w-6 h-6 mx-auto text-zinc-500 mb-1" /><span className="text-sm text-zinc-500">{uploading === k ? "Uploading…" : "Click to upload a banner"}</span></>}
      </div>
      <input ref={refEl} type="file" accept="image/*" onChange={(e) => uploadBanner(e, k)} className="hidden" data-testid={`${k}-file`} />
    </div>
  );

  const EmbedPreview = ({ color, title, body, banner, avatar, footer, name }) => (
    <div className="mt-2 rounded-md overflow-hidden border-l-4" style={{ borderColor: color, background: "#111214" }}>
      <div className="p-3 flex gap-3">
        <div className="flex-1">
          {title && <div className="font-semibold text-sm">{preview(title, name)}</div>}
          <div className="text-sm text-zinc-300 mt-1">{preview(body, name)}</div>
          {footer && <div className="text-[11px] text-zinc-500 mt-2">{preview(footer, name)}</div>}
        </div>
        {avatar && <img src="https://api.dicebear.com/7.x/bottts/svg?seed=NewMember" alt="" className="w-14 h-14 rounded-full bg-zinc-800 shrink-0" />}
      </div>
      {banner && <img src={src(banner)} alt="" className="w-full max-h-40 object-cover" />}
    </div>
  );

  return (
    <div>
      <PageHead title="Welcome & Goodbye" desc="Greet members as they join and bid them farewell as they leave.">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm"><span className="text-zinc-400">Module</span><Toggle testid="welcome-enabled" checked={cfg.enabled} onChange={(v) => setCfg({ ...cfg, enabled: v })} /></div>
          <button data-testid="welcome-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
        </div>
      </PageHead>

      <p className="text-xs text-zinc-600 mb-6">Variables: <code className="font-mono text-volt">{"{user}"}</code> <code className="font-mono text-volt">{"{count}"}</code> <code className="font-mono text-volt">{"{server}"}</code></p>

      {/* ================= WELCOME ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <h3 className="font-head font-semibold flex items-center gap-2"><LogIn className="w-4 h-4 text-volt" /> Welcome (member joins)</h3>
          <div><label className={lbl}>Welcome channel</label>
            <ChannelSelect guildId={guildId} value={c.welcome_channel} onChange={(v) => set("welcome_channel", v)} testid="welcome-channel" placeholder="— select a channel —" /></div>
          <div><label className={lbl}>Welcome message</label>
            <textarea data-testid="welcome-message" value={c.welcome_message || ""} onChange={(e) => set("welcome_message", e.target.value)} rows={3} className={field} /></div>
          <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">Also send as a DM</span><Toggle testid="dm-welcome" checked={!!c.dm_welcome} onChange={(v) => set("dm_welcome", v)} /></div>
          {c.dm_welcome && <div><label className={lbl}>DM message (optional — defaults to welcome message)</label>
            <textarea data-testid="dm-message" value={c.dm_message || ""} onChange={(e) => set("dm_message", e.target.value)} rows={2} className={field} /></div>}
          <div className="flex items-center justify-between"><span className="text-sm text-zinc-400">Show joining member's avatar</span><Toggle testid="show-avatar" checked={c.show_avatar !== false} onChange={(v) => set("show_avatar", v)} /></div>

          {/* Join roles */}
          <div className="border-t border-zinc-800 pt-4">
            <span className="text-sm font-semibold flex items-center gap-2 mb-2"><UserPlus className="w-4 h-4 text-volt" /> Roles on join</span>
            <div className="flex gap-2">
              <RoleSelect guildId={guildId} value={roleInput} onChange={setRoleInput} testid="join-role-input" placeholder="— select a role to add —" className="flex-1 text-sm" />
              <button data-testid="join-role-add" onClick={addRole} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 flex items-center gap-1 text-sm"><Plus className="w-4 h-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {roles.map((r) => (
                <span key={r} data-testid={`join-role-${r}`} className="flex items-center gap-1.5 bg-volt/10 text-volt rounded-full pl-3 pr-1.5 py-1 text-sm">{r}
                  <button onClick={() => set("join_roles", roles.filter((x) => x !== r))} className="hover:bg-volt/20 rounded-full p-0.5"><X className="w-3.5 h-3.5" /></button></span>
              ))}
            </div>
          </div>

          {/* Rich embed */}
          <div className="border-t border-zinc-800 pt-4 space-y-4">
            <div className="flex items-center justify-between"><span className="text-sm font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-volt" /> Rich embed with banner</span><Toggle testid="embed-enabled" checked={!!c.embed_enabled} onChange={(v) => set("embed_enabled", v)} /></div>
            {c.embed_enabled && (
              <>
                <div><label className={lbl}>Embed title</label>
                  <input data-testid="embed-title" value={c.embed_title || ""} onChange={(e) => set("embed_title", e.target.value)} className={field} /></div>
                <div><label className={lbl}>Embed footer</label>
                  <input data-testid="embed-footer" value={c.embed_footer || ""} onChange={(e) => set("embed_footer", e.target.value)} className={field} /></div>
                <div className="flex items-center gap-3"><label className={lbl}>Accent color</label>
                  <input type="color" data-testid="embed-color" value={c.embed_color || "#0EA5E9"} onChange={(e) => set("embed_color", e.target.value)} className="h-9 w-16 bg-black border border-zinc-800 rounded-md cursor-pointer" /></div>
                <Dropzone value={c.banner_url} k="banner_url" refEl={welcomeRef} />
              </>
            )}
          </div>

          {/* Buttons */}
          <div className="border-t border-zinc-800 pt-4">
            <span className="text-sm font-semibold flex items-center gap-2 mb-2"><Link2 className="w-4 h-4 text-volt" /> Link buttons</span>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="btn-label" value={btn.label} onChange={(e) => setBtn({ ...btn, label: e.target.value })} placeholder="Label (Read Rules)" className="bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm" />
              <div className="flex gap-2">
                <input data-testid="btn-url" value={btn.url} onChange={(e) => setBtn({ ...btn, url: e.target.value })} placeholder="https://…" className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt text-sm" />
                <button data-testid="btn-add" onClick={addBtn} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-3 flex items-center text-sm"><Plus className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {buttons.map((b, i) => (
                <span key={i} data-testid={`btn-chip-${i}`} className="flex items-center gap-1.5 bg-zinc-800 rounded-full pl-3 pr-1.5 py-1 text-sm">🔗 {b.label}
                  <button onClick={() => set("buttons", buttons.filter((_, j) => j !== i))} className="hover:bg-zinc-700 rounded-full p-0.5"><X className="w-3.5 h-3.5" /></button></span>
              ))}
            </div>
          </div>
        </div>

        {/* Welcome preview */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 h-fit">
          <h3 className="font-head font-semibold mb-4">Welcome Preview</h3>
          <div className="bg-black rounded-lg p-4">
            <div className="flex gap-3"><div className="w-10 h-10 rounded-full bg-volt/20 flex items-center justify-center text-volt font-bold">C</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-volt">Crank <span className="text-xs bg-volt/20 px-1 rounded ml-1">BOT</span></div>
                {c.embed_enabled
                  ? <EmbedPreview color={c.embed_color || "#0EA5E9"} title={c.embed_title} body={c.welcome_message} banner={c.banner_url} avatar={c.show_avatar !== false} footer={c.embed_footer} />
                  : <div className="text-sm text-zinc-200 mt-1">{preview(c.welcome_message)}</div>}
                {buttons.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{buttons.map((b, i) => <span key={i} className="text-xs bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 flex items-center gap-1"><Link2 className="w-3 h-3" />{b.label}</span>)}</div>}
              </div></div>
          </div>
          {c.join_log_channel && <p className="text-xs text-zinc-600 mt-3">Join log → <span className="text-volt">#{c.join_log_channel}</span></p>}
        </div>
      </div>

      {/* ================= GOODBYE ================= */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-head font-semibold flex items-center gap-2"><LogOut className="w-4 h-4 text-red-400" /> Goodbye (member leaves)</h3>
            <Toggle testid="goodbye-enabled" checked={c.goodbye_enabled !== false} onChange={(v) => set("goodbye_enabled", v)} />
          </div>
          {c.goodbye_enabled !== false && (
            <>
              <div><label className={lbl}>Goodbye channel</label>
                <ChannelSelect guildId={guildId} value={c.goodbye_channel} onChange={(v) => set("goodbye_channel", v)} testid="goodbye-channel" placeholder="— select a channel —" /></div>
              <div><label className={lbl}>Goodbye message</label>
                <textarea data-testid="goodbye-message" value={c.goodbye_message || ""} onChange={(e) => set("goodbye_message", e.target.value)} rows={2} className={field} /></div>
              <div className="border-t border-zinc-800 pt-4 space-y-4">
                <div className="flex items-center justify-between"><span className="text-sm font-semibold flex items-center gap-2"><ImageIcon className="w-4 h-4 text-red-400" /> Rich embed</span><Toggle testid="goodbye-embed-enabled" checked={!!c.goodbye_embed_enabled} onChange={(v) => set("goodbye_embed_enabled", v)} /></div>
                {c.goodbye_embed_enabled && (
                  <>
                    <div><label className={lbl}>Embed title</label>
                      <input data-testid="goodbye-embed-title" value={c.goodbye_embed_title || ""} onChange={(e) => set("goodbye_embed_title", e.target.value)} className={field} /></div>
                    <div className="flex items-center gap-3"><label className={lbl}>Accent color</label>
                      <input type="color" data-testid="goodbye-embed-color" value={c.goodbye_embed_color || "#ef4444"} onChange={(e) => set("goodbye_embed_color", e.target.value)} className="h-9 w-16 bg-black border border-zinc-800 rounded-md cursor-pointer" /></div>
                    <Dropzone value={c.goodbye_banner_url} k="goodbye_banner_url" refEl={goodbyeRef} />
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Goodbye preview */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 h-fit">
          <h3 className="font-head font-semibold mb-4">Goodbye Preview</h3>
          {c.goodbye_enabled !== false ? (
            <div className="bg-black rounded-lg p-4">
              <div className="flex gap-3"><div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 font-bold">C</div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-volt">Crank <span className="text-xs bg-volt/20 px-1 rounded ml-1">BOT</span></div>
                  {c.goodbye_embed_enabled
                    ? <EmbedPreview color={c.goodbye_embed_color || "#ef4444"} title={c.goodbye_embed_title} body={c.goodbye_message} banner={c.goodbye_banner_url} avatar name="LeavingUser" />
                    : <div className="text-sm text-zinc-200 mt-1">{preview(c.goodbye_message, "LeavingUser")}</div>}
                </div></div>
            </div>
          ) : <p className="text-sm text-zinc-600">Goodbye messages are turned off.</p>}
        </div>
      </div>

      {/* ================= LOGS ================= */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-2xl">
        <h3 className="font-head font-semibold flex items-center gap-2 mb-4"><ScrollText className="w-4 h-4 text-volt" /> Join / Leave logs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><label className={lbl}>Join log channel</label>
            <input data-testid="join-log-channel" value={c.join_log_channel || ""} onChange={(e) => set("join_log_channel", e.target.value)} className={field} placeholder="e.g. member-log (blank = off)" /></div>
          <div><label className={lbl}>Leave log channel</label>
            <input data-testid="leave-log-channel" value={c.leave_log_channel || ""} onChange={(e) => set("leave_log_channel", e.target.value)} className={field} placeholder="e.g. member-log (blank = off)" /></div>
        </div>
        <p className="text-xs text-zinc-600 mt-3">Logs post a rich embed (avatar, account age, member count) to the channel you name here.</p>
      </div>
    </div>
  );
}
